import React, { useEffect, useState, Suspense, useTransition, useCallback } from "react";
import axios from "axios";
import "./css/DashboardPage.css";
import logo from "../logo.png";
import {FiFileText, FiLogOut} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import ProfilerKeywordSearch from "./SearchResults";
import { MdVisibility, MdBuildCircle, MdInsights } from "react-icons/md";
import {
    FaBuilding,
    FaBoxOpen,
    FaBalanceScale,
    FaChartLine,
    FaCoins,
    FaFileWord,
    FaFilePdf, FaSitemap, FaBookOpen, FaGlobe,
} from "react-icons/fa";
import TabbedCategoryCardGroup from "./TabbedCategoryCardGroup";
import { Newspaper, Printer, MoreVertical, BookOpen, HelpCircle, Bell, Landmark } from "lucide-react";
import QuickGuides from "./QuickGuides";
import HelpSettingsPage from "./HelpSettingsPage";
import IndustryNews from "./IndustryNews";
import { useTearsheetManager } from "./useTearsheetManager";
import TearsheetDock from "./TearsheetDock";
import ExpertInsights from "./ExpertInsights";
import ParentCompany from "./ParentCompaniesPage";
import GlossaryPage from "./GlossaryPage";
import WebsitesPage from "./WebsitesPage";
import TearsheetsNews from "./TearsheetsPage";
import NotesAndCommentaryExport from "./NotesAndCommentaryExport";
import {TbFileExport} from "react-icons/tb";
import MyProfilerPage from "./MyProfilerPage.tsx";
import ExchangesScreen from "./ExchangesScreen";
import { useSaveSearchDialog } from "./SaveSearchDialog";
import { FaDatabase, FaLeaf } from "react-icons/fa";
import EsgCoveragePanel from "./EsgCoveragePanel";
import {
    clearSession,
    getSessionBoolean,
    getSessionExpiry,
    getSessionString,
    getSessionToken,
    hasActiveSession
} from "../utils/session";


const VendorTearsheet = React.lazy(() => import("./VendorTearsheet"));
const ProductsListTearsheet = React.lazy(() => import("./ProductsListTearsheet"));
const ProductTearsheet = React.lazy(() => import("./ProductTearsheet"));
const DataTearsheet = React.lazy(() => import("./DataTearsheet"));

const MemoProfilerKeywordSearch = React.memo(ProfilerKeywordSearch);
const MemoIndustryNews = React.memo(IndustryNews);
const MemoQuickGuides = React.memo(QuickGuides);
const MemoExpertInsights = React.memo(ExpertInsights);
const MemoParentCompany = React.memo(ParentCompany);
const MemoGlossary = React.memo(GlossaryPage);
const MemoWebsites = React.memo(WebsitesPage);
const MemoTearsheets = React.memo(TearsheetsNews);
const MemoNotesCommentary = React.memo(NotesAndCommentaryExport);
const MemoMyProfiler = React.memo(MyProfilerPage);
const MemoExchanges = React.memo(ExchangesScreen);
const MemoHelpSettingsPage = React.memo(HelpSettingsPage);


const HEADER_TABS = [
    "Profiler",
    "XTVM",
    "ALT DATA",
    "Indices & Benchmarks",
];

const REDIRECT_IDS = {
    "XTVM": 'exchange',
    "ALT DATA": '3',
};

const RIGHT_TABS = [
    { label: "Vendor Category", icon: <FaBuilding /> },
    { label: "Product Category", icon: <FaBoxOpen /> },
    { label: "Fixed Income", icon: <FaBalanceScale /> },
    { label: "Equity", icon: <FaChartLine /> },
    { label: "Commodity", icon: <FaCoins /> },
];

const PRODUCT_CATEGORY_TABS = [
    { label: "Product Type", categoryId: 1 },
    { label: "Delivery", categoryId: 2 },
    { label: "Data Type", categoryId: 4 },
    { label: "Market", categoryId: 6 },
    { label: "Region", categoryId: 7 },
    { label: "Tier", categoryId: 3 },
];
const PRODUCT_CATEGORY_TABS_ALT_DATA = [
    { label: "Product Type", categoryId: 19 },
    { label: "Delivery", categoryId: 23 },
    { label: "Data Type", categoryId: 24 },
    { label: "Provider Type", categoryId:20 },
    { label: "Market", categoryId: 22 },
    { label: "Region", categoryId: 21 },
];
const VENDOR_CATEGORY_TABS = [{ label: "Vendor Type", categoryId: 8 }];

const RIGHT_TABS_PROFILER = [
    { label: "Vendor Category", icon: <FaBuilding /> },
    { label: "Product Category", icon: <FaBoxOpen /> },
    { label: "Fixed Income", icon: <FaBalanceScale /> },
    { label: "Equity", icon: <FaChartLine /> },
    { label: "Commodity", icon: <FaCoins /> },
];


const RIGHT_TABS_ALTDATA = [
    { label: "ESG Vendor Category", icon: <FaBuilding /> }, // categoryId=26
    { label: "Product Category", icon: <FaBoxOpen /> }, // mode=altdata
    { label: "Data/Functionality Coverage", icon: <FaDatabase /> }, // fct=9 dataFuncSearch
    { label: "ESG Data/Functionality Coverage", icon: <FaLeaf /> }, // fct=10 esgSearch
];

const MODULE_IDS = {
    "Fixed Income": 1,
    Equity: 2,
    Commodity: 3,
    "Data/Functionality Coverage": 9,
    "ESG Data/Functionality Coverage": 10,
};

const MODULE_LABELS = Object.fromEntries(
    Object.entries(MODULE_IDS).map(([label, id]) => [id, label])
);

const SEARCH_REQUEST_TYPES = {
    DISCOVER: "discover",
    PRODUCT: "product",
    VENDOR: "vendor",
};

// Vendor category tabs:
// profiler => categoryId=8
// altdata => ESG Vendor Category => categoryId=26
const VENDOR_CATEGORY_TABS_PROFILER = [{ label: "Vendor Type", categoryId: 8 }];
const VENDOR_CATEGORY_TABS_ALTDATA  = [{ label: "ESG Vendor Type", categoryId: 26 }];


const PRODUCT_CATEGORY_TABS_PROFILER = PRODUCT_CATEGORY_TABS;
const PRODUCT_CATEGORY_TABS_ALTDATA  = PRODUCT_CATEGORY_TABS_ALT_DATA;


const HeaderQuickMenu = React.memo(function HeaderQuickMenu({
                                                                isAltDataMode = false,
                                                                isExpertInsightsActive,
                                                                onPickQuickGuides,
                                                                onPickIndustryNews,
                                                                onPickParentCompany,
                                                                onPickGlossary,
                                                                onPickWebsites,
                                                                onPickTearsheets,
                                                                onPickNotesCommentary,
                                                                onPickMyProfiler,
                                                                onPickExchanges,
                                                                onPickHelpSettings,
                                                                onOpenExpertInsights,
                                                            }) {
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef(null);

    React.useEffect(() => {
        if (!open) return;
        const onDocDown = (e) => {
            if (!rootRef.current?.contains(e.target)) setOpen(false);
        };
        document.addEventListener("pointerdown", onDocDown, { capture: true });
        return () => document.removeEventListener("pointerdown", onDocDown, { capture: true });
    }, [open]);

    const showProfilerOnly = !isAltDataMode;
    return (
        <div className="ml-auto relative flex items-center gap-0" ref={rootRef}>
            <button
                onClick={onOpenExpertInsights}
                title="Open Expert Insights"
                className={`inline-flex items-center gap-2 text-sm font-medium
        text-[var(--rusty-orange)] pl-2 pr-1.5 py-1 rounded-lg transition-colors
        hover:bg-[var(--rusty-orange)]/10 focus:outline-none
        focus:ring-2 focus:ring-[var(--rusty-orange)]/30
        ${isExpertInsightsActive ? "font-semibold" : ""}`}
            >
                <MdInsights className="h-4 w-4" aria-hidden="true" />
                <span>Expert Insights</span>
            </button>

            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center justify-center p-1.5 -ml-1 rounded-md
                 text-[#25408f] hover:bg-[#25408f]/10 focus:outline-none
                 focus:ring-2 focus:ring-[#25408f]/30"
                title="More"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <MoreVertical className="w-5 h-5" />
            </button>

            {open && (
                <div
                    role="menu"
                    className="absolute right-0 top-full mt-1 w-52 overflow-hidden rounded-lg
                   border border-gray-200 bg-white shadow-lg z-50 will-change-transform"
                    onClick={() => setOpen(false)}
                >
                    <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={onPickQuickGuides}
                        role="menuitem"
                    >
                        <BookOpen className="w-4 h-4 text-[#25408f]" />
                        <span>Quick Guides</span>
                    </button>
                    <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={onPickIndustryNews}
                        role="menuitem"
                    >
                        <Newspaper className="w-4 h-4 text-[#25408f]"/>
                        <span>Industry News</span>
                    </button>
                    { showProfilerOnly && (<button
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={onPickParentCompany}
                        role="menuitem"
                    >
                        <FaSitemap className="w-4 h-4 text-[#25408f]"/>
                        <span>Parent Companies</span>
                    </button>
                    )}
                    <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={onPickGlossary}
                        role="menuitem"
                    >
                        <FaBookOpen className="w-4 h-4 text-[#25408f]" />
                        <span>Glossary</span>
                    </button>
                    { showProfilerOnly && (<button
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={onPickWebsites}
                        role="menuitem"
                    >
                        <FaGlobe className="w-4 h-4 text-[#25408f]" />
                        <span>Website Listing</span>
                    </button>
                    )}
                    { showProfilerOnly && (<button
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={onPickTearsheets}
                        role="menuitem"
                    >
                        <FiFileText className="w-4 h-4 text-[#25408f]" />
                        <span>New – Updated Tearsheets</span>
                    </button>
                     )}
                    { showProfilerOnly && (<button
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={onPickNotesCommentary}
                        role="menuitem"
                    >
                        <TbFileExport className="w-4 h-4 text-[#25408f]" />
                        <span>Commentary Export</span>
                    </button>
                    )}
                    <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={onPickMyProfiler}
                        role="menuitem"
                    >
                        <Bell className="w-4 h-4 text-[#25408f]" />
                        <span>My Profiler</span>
                    </button>
                    { showProfilerOnly && (<button
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={onPickExchanges}
                        role="menuitem"
                    >
                        <Landmark className="w-4 h-4 text-[#25408f]" />
                        <span>Exchanges</span>
                    </button>
                     )}
                    <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={onPickHelpSettings}
                        role="menuitem"
                    >
                        <HelpCircle className="w-4 h-4 text-[#25408f]" />
                        <span>Help & Settings</span>
                    </button>
                </div>
            )}
        </div>
    );

});

const RightSkeleton = () => (
    <div className="p-4 space-y-3 animate-pulse">
        <div className="h-5 w-40 bg-gray-200 rounded" />
        <div className="h-4 w-full bg-gray-200 rounded" />
        <div className="h-4 w-5/6 bg-gray-200 rounded" />
        <div className="h-4 w-3/4 bg-gray-200 rounded" />
    </div>
);

const RightPanel = React.memo(function RightPanel({
                                                      activeTearsheetTab,
                                                      setActiveTearsheetTab,
                                                      tearsheetVendorId,
                                                      productsVendorId,
                                                      tearsheetProductId,
                                                      tearsheetDataId,
                                                      openProductsTearsheet,
                                                      openProductTearsheet,
                                                      openDataTearsheet,
                                                      handlePrint, handleDownloadWord, handleDownloadPdf,highlight,
                                                      mode = "profiler"
                                                  }) {
    const [mountedTabs, setMountedTabs] = React.useState({});
    const [isPending, startTransition] = useTransition();

    React.useEffect(() => {
        setMountedTabs((m) => (m[activeTearsheetTab] ? m : { ...m, [activeTearsheetTab]: true }));
    }, [activeTearsheetTab]);

    const TabBtn = ({ tabKey, label, onClick }) => (
        <button
            className={`px-4 py-2 -mb-px font-semibold focus:outline-none ${
                activeTearsheetTab === tabKey
                    ? "text-[var(--rusty-orange)] border-b-2 border-[var(--rusty-orange)]"
                    : "text-gray-600"
            }`}
            onClick={() => startTransition(() => (onClick ? onClick() : setActiveTearsheetTab(tabKey)))}
        >
            {label}
        </button>
    );

    return (
        <div className="flex flex-col h-full min-h-0">
            <div className="flex justify-between items-center border-b">
                <div className="tabs flex">
                    <TabBtn tabKey="Vendor" label="Vendor" />
                    <TabBtn tabKey="Products" label="Products" onClick={() => openProductsTearsheet?.(tearsheetVendorId)} />
                    <TabBtn tabKey="Product" label="Product" onClick={() => openProductTearsheet?.(productsVendorId)} />
                    <TabBtn tabKey="Data" label="Data" onClick={() => openDataTearsheet?.(tearsheetProductId)} />
                    <TabBtn tabKey="Functionality" label="Functionality" />
                </div>
                <div className="flex space-x-4 items-center">
                    <Printer className="w-5 h-5 cursor-pointer text-gray-600 hover:text-[var(--rusty-orange)]" onClick={handlePrint} />
                    <FaFileWord className="w-5 h-5 cursor-pointer text-gray-600 hover:text-[var(--rusty-orange)]" onClick={handleDownloadWord} />
                    <FaFilePdf  className="w-5 h-5 cursor-pointer text-gray-600 hover:text-[var(--rusty-orange)]" onClick={handleDownloadPdf} />
                </div>
            </div>

            <div className="tab-content mt-4 flex-1 min-h-0 overflow-auto">
                <Suspense fallback={<RightSkeleton />}>
                    <div className={activeTearsheetTab === "Vendor" ? "block" : "hidden"}>
                        {mountedTabs.Vendor && tearsheetVendorId && (
                            <VendorTearsheet
                                vendorId={tearsheetVendorId}
                                contextPath={process.env.REACT_APP_API_URL}
                                onClose={() => {}}
                                onOpenProductPopup={(id) => openProductTearsheet?.(id)}
                                highlight={highlight}
                                mode={mode}
                            />
                        )}
                    </div>

                    <div className={activeTearsheetTab === "Products" ? "block" : "hidden"}>
                        {mountedTabs.Products && productsVendorId && (
                            <ProductsListTearsheet
                                vendorId={productsVendorId}
                                onClose={() => {}}
                                onProductClick={(id) => openProductTearsheet?.(id)}
                            />
                        )}
                    </div>

                    <div className={activeTearsheetTab === "Product" ? "block" : "hidden"}>
                        {mountedTabs.Product && tearsheetProductId && (
                            <ProductTearsheet
                                productId={tearsheetProductId}
                                contextPath={process.env.REACT_APP_API_URL}
                                onClose={() => {}}
                                onOpenProductTearsheet={(id) => openProductTearsheet?.(id)}
                                onOpenVendorTearsheet={() => {}}
                                highlight={highlight}
                                mode={mode}
                            />
                        )}
                    </div>

                    <div className={activeTearsheetTab === "Data" ? "block" : "hidden"}>
                        {mountedTabs.Data && tearsheetDataId && (
                            <DataTearsheet
                                dataId={tearsheetDataId}
                                contextPath={process.env.REACT_APP_API_URL}
                                onClose={() => {}}
                            />
                        )}
                    </div>

                    <div className={activeTearsheetTab === "Functionality" ? "block" : "hidden"}>
                        {mountedTabs.Functionality && (
                            <div className="text-sm text-gray-600">Functionality coming soon…</div>
                        )}
                    </div>
                </Suspense>
            </div>

            {isPending && <div className="mt-2 text-xs text-gray-500">Loading…</div>}
        </div>
    );
});

const Chip = React.memo(function Chip({ label, onRemove }) {
    return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-[rgba(37,64,143,0.25)] text-[11px]">
      <span className="max-w-[180px] truncate" title={label}>{label}</span>
      <button
          type="button"
          className="leading-none px-1 hover:text-[var(--rusty-orange)]"
          onClick={onRemove}
          title="Remove"
      >
        ×
      </button>
    </span>
    );
});

const FEATURE_MODULE_GROUPS = [
    { moduleId: 1, label: "Fixed Income" },
    { moduleId: 2, label: "Equity" },
    { moduleId: 3, label: "Commodity" },
    { moduleId: 9, label: "Data/Functionality Coverage" },
    { moduleId: 10, label: "ESG Data/Functionality Coverage" },
];

const createEmptyFeatureIdsByModule = () =>
    FEATURE_MODULE_GROUPS.reduce((acc, { moduleId }) => {
        acc[moduleId] = [];
        return acc;
    }, {});

const SelectedFiltersTray = React.memo(function SelectedFiltersTray({
                                                                        vendorIds = [],
                                                                        productIds = [],
                                                                        featureIdsByModule = createEmptyFeatureIdsByModule(),
                                                                        categoryItemNameCache = {},
                                                                        featureNameCache = {},
                                                                        keywordRequests = [],
                                                                        onRemoveVendor,
                                                                        onRemoveProduct,
                                                                        onRemoveFeature, // (moduleId, featureId) => void
                                                                        onRemoveKeywordItem,
                                                                        onClearFilters,
                                                                    }) {
    const groups = FEATURE_MODULE_GROUPS.map(({ moduleId, label }) => ({
        moduleId,
        label,
        ids: featureIdsByModule?.[moduleId] || [],
    }));

    const targetLabelFor = (target) =>
        target === "vendors_only" || target === "vendor"
            ? "Vendors only"
            : target === "products_only" || target === "product"
                ? "Products only"
                : "Both";

    const hasAny =
        keywordRequests.length > 0 ||
        vendorIds.length ||
        productIds.length ||
        groups.some((g) => (g.ids || []).length);

    if (!hasAny) return null;

    const nameForCat = (id) => categoryItemNameCache[id] || `#${id}`;
    const nameForFeat = (id) => featureNameCache[id] || `#${id}`;

    return (
        <div className="border-t border-gray-200 bg-white/95 backdrop-blur-sm px-3 py-3">
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-[var(--threedi-blue,#25408f)]">
                    Selected filters
                </div>
                <button
                    type="button"
                    className="text-[11px] underline text-[var(--rusty-orange)]"
                    onClick={onClearFilters}
                >
                    Clear filters
                </button>
            </div>

            {keywordRequests.length > 0 && (
                <div className="mb-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">
                        Keyword Search -- (Apply to Vendors and/or Products)
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {keywordRequests.map((request) => (
                            <Chip
                                key={`kw-${request._historyIndex}`}
                                label={`${String(request?.payload?.keyword || "").trim()} (${targetLabelFor(request?.payload?.keywordSearchType)})`}
                                onRemove={() => onRemoveKeywordItem?.(request)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {vendorIds.length > 0 && (
                <div className="mb-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">
                        Vendor Category
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {vendorIds.map((id) => (
                            <Chip
                                key={`v-${id}`}
                                label={nameForCat(id)}
                                onRemove={() => onRemoveVendor(id)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {productIds.length > 0 && (
                <div className="mb-2">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">
                        Product Category
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {productIds.map((id) => (
                            <Chip
                                key={`p-${id}`}
                                label={nameForCat(id)}
                                onRemove={() => onRemoveProduct(id)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {groups.map((g) =>
                g.ids.length ? (
                    <div className="mb-2" key={`feat-${g.moduleId}`}>
                        <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">
                            {g.label}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {g.ids.map((id) => (
                                <Chip
                                    key={`f-${g.moduleId}-${id}`}
                                    label={nameForFeat(id)}
                                    onRemove={() => onRemoveFeature(g.moduleId, id)}
                                />
                            ))}
                        </div>
                    </div>
                ) : null
            )}
        </div>
    );
});



export default function DashboardPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("Profiler");
    const [activeRightTab, setActiveRightTab] = useState("Vendor Category");
    const [viewMode, setViewMode] = useState("Discovery Builder");
    const isAltDataMode = activeTab === "ALT DATA";
    const mode = isAltDataMode ? "altdata" : "profiler";

    const tear = useTearsheetManager(isAltDataMode ? "altdata_tearsheets" : "profiler_tearsheets");

    const [selectedVendorCategoryIds, setSelectedVendorCategoryIds] = useState([]);
    const [selectedProductCategoryIds, setSelectedProductCategoryIds] = useState([]);
    const [hasSubmittedSearch, setHasSubmittedSearch] = useState(false);
    const [keywordSearchValue, setKeywordSearchValue] = useState("");
    const [discoverKeywordTarget, setDiscoverKeywordTarget] = useState("both");
    const [searchRequestHistory, setSearchRequestHistory] = useState([]);
    const [searchHistoryReplayToken, setSearchHistoryReplayToken] = useState(0);
    const [searchResultsClearToken, setSearchResultsClearToken] = useState(0);
    const [featureTabsMap, setFeatureTabsMap] = useState({});
    const [tearsheetVendorId, setTearsheetVendorId] = useState(null);
    const [productsVendorId, setProductsVendorId] = useState(null);
    const [tearsheetProductId, setTearsheetProductId] = useState(null);
    const [tearsheetDataId, setTearsheetDataId] = useState(null);
    const [activeTearsheetTab, setActiveTearsheetTab] = useState("Vendor");
    const [featureIdsByModule, setFeatureIdsByModule] = useState(() => createEmptyFeatureIdsByModule());
    const [rightPanelResetKey, setRightPanelResetKey] = useState(0);
    const pendingKeywordRef = React.useRef(null);
    const pendingKeywordDetailRef = React.useRef(null);
    const previousSubmittedSearchInputsRef = React.useRef(null);
    const searchRequestHistoryRef = React.useRef([]);
    const suppressSelectionAutoSearchRef = React.useRef(false);

    const { openSaveDialog, saveDialogPortal } = useSaveSearchDialog();

    const [compareProducts, setCompareProducts] = useState([]);

    const compareProductIds = compareProducts.map(p => p.id);

    const [keywordHighlight, setKeywordHighlight] = useState(null);
    const [esgCoverageEsgIds, setEsgCoverageEsgIds] = useState([]);
    const [esgCoverageAltIds, setEsgCoverageAltIds] = useState([]);


    const rightTabs = isAltDataMode ? RIGHT_TABS_ALTDATA : RIGHT_TABS_PROFILER;

    const vendorTabs = isAltDataMode ? VENDOR_CATEGORY_TABS_ALTDATA : VENDOR_CATEGORY_TABS_PROFILER;
    const productTabs = isAltDataMode ? PRODUCT_CATEGORY_TABS_ALTDATA : PRODUCT_CATEGORY_TABS_PROFILER;

    const replaceSearchRequestHistory = useCallback((nextHistory) => {
        const normalized = Array.isArray(nextHistory) ? nextHistory : [];
        searchRequestHistoryRef.current = normalized;
        setSearchRequestHistory(normalized);
    }, []);

    const upsertSearchRequestHistory = useCallback((entry) => {
        if (!entry?.historyType) return;

        const normalizedHistoryType = String(entry.historyType || "").trim().toLowerCase();
        const isKeywordHistory =
            normalizedHistoryType === SEARCH_REQUEST_TYPES.DISCOVER ||
            normalizedHistoryType === SEARCH_REQUEST_TYPES.PRODUCT ||
            normalizedHistoryType === SEARCH_REQUEST_TYPES.VENDOR;

        const hadPreviousKeywordEntries = isKeywordHistory && searchRequestHistoryRef.current.some((r) => {
            const ht = String(r?.historyType || "").trim().toLowerCase();
            return ht === SEARCH_REQUEST_TYPES.DISCOVER ||
                ht === SEARCH_REQUEST_TYPES.PRODUCT ||
                ht === SEARCH_REQUEST_TYPES.VENDOR;
        });

        const nextHistory = isKeywordHistory
            ? [...searchRequestHistoryRef.current, entry]
            : [
                ...searchRequestHistoryRef.current.filter((request) => request?.historyType !== entry.historyType),
                entry,
            ];
        replaceSearchRequestHistory(nextHistory);

        if (hadPreviousKeywordEntries) {
            suppressSelectionAutoSearchRef.current = true;
            setHasSubmittedSearch(false);
            setSearchHistoryReplayToken((value) => value + 1);
        }
    }, [replaceSearchRequestHistory]);

    const replaySearchRequestHistory = useCallback((nextHistory) => {
        replaceSearchRequestHistory(nextHistory);

        if (nextHistory.length > 0) {
            suppressSelectionAutoSearchRef.current = true;
            setHasSubmittedSearch(false);
            setSearchHistoryReplayToken((value) => value + 1);
            return;
        }

        setHasSubmittedSearch(true);
    }, [replaceSearchRequestHistory]);

    const keywordRequestItems = React.useMemo(() => {
        return searchRequestHistory
            .map((request, index) => ({ ...request, _historyIndex: index }))
            .filter((request) => {
                const historyType = String(request?.historyType || "").trim().toLowerCase();
                const keyword = String(request?.payload?.keyword || "").trim();
                return (
                    (
                        historyType === SEARCH_REQUEST_TYPES.DISCOVER ||
                        historyType === SEARCH_REQUEST_TYPES.PRODUCT ||
                        historyType === SEARCH_REQUEST_TYPES.VENDOR
                    ) &&
                    keyword.length > 0
                );
            });
    }, [searchRequestHistory]);

    const handleClearAll = React.useCallback(() => {
        suppressSelectionAutoSearchRef.current = true;
        setHasSubmittedSearch(false);
        setSelectedVendorCategoryIds([]);
        setSelectedProductCategoryIds([]);
        setFeatureIdsByModule(createEmptyFeatureIdsByModule());
        setKeywordHighlight(null);
        setTearsheetVendorId(null);
        setProductsVendorId(null);
        setTearsheetProductId(null);
        setTearsheetDataId(null);
        setEsgCoverageEsgIds([]);
        setEsgCoverageAltIds([]);
        setActiveTearsheetTab("Vendor");
        setKeywordSearchValue("");
        setDiscoverKeywordTarget("both");
        replaceSearchRequestHistory([]);

        setViewMode("Discovery Builder");
        setActiveRightTab(isAltDataMode ? "ESG Vendor Category" : "Vendor Category");
        setRightPanelResetKey(k => k + 1);
    }, [replaceSearchRequestHistory]);


    const allFeatureIds = React.useMemo(() => {
        const flat = Object.values(featureIdsByModule || {}).flat();
        return Array.from(new Set(flat.map(Number).filter(Number.isFinite)));
    }, [featureIdsByModule]);

    useEffect(() => {
        const nextInputsKey = JSON.stringify({
            featureIds: allFeatureIds,
            vendorCategoryIds: selectedVendorCategoryIds,
            productCategoryIds: selectedProductCategoryIds,
        });

        if (previousSubmittedSearchInputsRef.current === null) {
            previousSubmittedSearchInputsRef.current = nextInputsKey;
            return;
        }

        if (previousSubmittedSearchInputsRef.current === nextInputsKey) {
            return;
        }

        previousSubmittedSearchInputsRef.current = nextInputsKey;

        if (suppressSelectionAutoSearchRef.current) {
            suppressSelectionAutoSearchRef.current = false;
            return;
        }

        setHasSubmittedSearch(true);
    }, [allFeatureIds, selectedVendorCategoryIds, selectedProductCategoryIds]);


    const [categoryItemNameCache, setCategoryItemNameCache] = useState({});
    const [featureNameCache, setFeatureNameCache] = useState({});

    const fetchCategoryItemNames = useCallback(async (ids) => {
        if (!Array.isArray(ids) || !ids.length) return;

        const missing = ids.filter((id) => !categoryItemNameCache[id]);
        if (!missing.length) return;

        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/category-items/names`, {
                params: { ids: missing.join(",") },
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });

            const next = {};
            if (Array.isArray(res.data)) {
                // supports [{id,name}] or [{id,item}]
                res.data.forEach((x) => {
                    const id = Number(x?.id);
                    const name = String(x?.name || x?.item || "").trim();
                    if (Number.isFinite(id) && name) next[id] = name;
                });
            } else if (res.data && typeof res.data === "object") {
                // supports { [id]: name }
                Object.entries(res.data).forEach(([k, v]) => {
                    const id = Number(k);
                    const name = String(v || "").trim();
                    if (Number.isFinite(id) && name) next[id] = name;
                });
            }

            if (Object.keys(next).length) {
                setCategoryItemNameCache((prev) => ({ ...prev, ...next }));
            }
        } catch (e) {
            console.error("Failed to fetch category item names", e);
        }
    }, [categoryItemNameCache]);

    const fetchFeatureNames = useCallback(async (ids) => {
        if (!Array.isArray(ids) || !ids.length) return;

        const missing = ids.filter((id) => !featureNameCache[id]);
        if (!missing.length) return;

        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/features/names`, {
                params: { ids: missing.join(",") },
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });

            const next = {};
            if (Array.isArray(res.data)) {
                // supports [{id,name}]
                res.data.forEach((x) => {
                    const id = Number(x?.id);
                    const name = String(x?.name || "").trim();
                    if (Number.isFinite(id) && name) next[id] = name;
                });
            } else if (res.data && typeof res.data === "object") {
                // supports { [id]: name }
                Object.entries(res.data).forEach(([k, v]) => {
                    const id = Number(k);
                    const name = String(v || "").trim();
                    if (Number.isFinite(id) && name) next[id] = name;
                });
            }

            if (Object.keys(next).length) {
                setFeatureNameCache((prev) => ({ ...prev, ...next }));
            }
        } catch (e) {
            console.error("Failed to fetch feature names", e);
        }
    }, [featureNameCache]);

    const moduleId = MODULE_IDS[activeRightTab] || null;


    const handleBeforeKeywordSearch = useCallback(() => {
        setSelectedVendorCategoryIds([]);
        setSelectedProductCategoryIds([]);
        setFeatureIdsByModule(createEmptyFeatureIdsByModule());
    }, []);

    const [saveableCtx, setSaveableCtx] = useState(null);

    const authHeaders = useCallback(() => {
        const t = getSessionToken();
        return t ? { Authorization: `Bearer ${t}` } : {};
    }, []);

    const saveSearch = useCallback(async (body) => {
        await axios.post(
            `${process.env.REACT_APP_API_URL}/save-searches`,
            body,
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
        window.dispatchEvent(new CustomEvent("profiler:saved-search-created"));
    }, []);


    useEffect(() => {
        const allCatIds = Array.from(
            new Set([...(selectedVendorCategoryIds || []), ...(selectedProductCategoryIds || [])])
        ).filter(Number.isFinite);

        if (allCatIds.length) fetchCategoryItemNames(allCatIds);
    }, [selectedVendorCategoryIds, selectedProductCategoryIds, fetchCategoryItemNames]);

    useEffect(() => {
        if (allFeatureIds.length) fetchFeatureNames(allFeatureIds);
    }, [allFeatureIds, fetchFeatureNames]);


    useEffect(() => {
        const onCtx = (e) => {
            const ctx = e?.detail || null;
            setSaveableCtx(ctx);

            const primaryKeyword = String(ctx?.keyword || "").trim();
            const extraKeywords = Array.isArray(ctx?.keywords) ? ctx.keywords : [];
            const hasAnyKeyword =
                !!primaryKeyword ||
                extraKeywords.some((k) => String(k || "").trim().length > 0);

            if (ctx?.type === "keyword" && hasAnyKeyword) {
                setKeywordHighlight({
                    keyword: primaryKeyword,
                    keywords: extraKeywords,
                    match: ctx.match || "all",
                });
            } else {
                setKeywordHighlight(null);
            }
        };

        window.addEventListener("profiler:results-context", onCtx);
        return () => window.removeEventListener("profiler:results-context", onCtx);
    }, []);

    // Clear all tearsheets when the browser tab/window is closed
    useEffect(() => {
        const handleBeforeUnload = () => {
            tear.clearAllSheets();
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [tear]);

    useEffect(() => {
        if (!hasActiveSession()) {
            navigate("/", { replace: true });
            return undefined;
        }

        const expiry = getSessionExpiry();
        if (!expiry) {
            clearSession();
            navigate("/", { replace: true });
            return undefined;
        }

        const timeoutMs = Math.max(expiry - Date.now(), 0);
        const timeoutId = window.setTimeout(() => {
            tear.clearAllSheets();
            clearSession();
            navigate("/", { replace: true });
        }, timeoutMs);

        return () => window.clearTimeout(timeoutId);
    }, [navigate, tear]);


    const handleSaveCurrentSearch = useCallback(async () => {
        if (!saveableCtx) return;

        // Keyword
        if (saveableCtx.type === "keyword") {
            const kw = (saveableCtx.keyword || "").trim();
            if (!kw) return;
            const match = saveableCtx.match || "all";
            const comparisonType = match === "any" ? 7 : match === "exact" ? 9 : 8;

            const name = window.prompt("Save this search as…", kw);
            if (!name) return;

            await saveSearch({ name, comparisonType, keyword: kw });
            return;
        }

        // Product Category
        if (saveableCtx.type === "product-category") {
            const ids = (saveableCtx.categoryItemIds || []).map(Number).filter(Number.isFinite);
            if (!ids.length) return;

            const names = saveableCtx.categoryItemNames;
            const suggested = Array.isArray(names) && names.length
                ? names.join(", ")
                : ids.join(",");

            const name = window.prompt("Save this search as…", suggested);
            if (!name) return;

            await saveSearch({ name, comparisonType: 3, categoryItemIds: ids });
            return;
        }

        // Vendor Category
        if (saveableCtx.type === "vendor-category") {
            const ids = (saveableCtx.categoryItemIds || []).map(Number).filter(Number.isFinite);
            if (!ids.length) return;

            const names = saveableCtx.categoryItemNames;
            const suggested = Array.isArray(names) && names.length
                ? names.join(", ")
                : ids.join(",");

            const name = window.prompt("Save this search as…", suggested);
            if (!name) return;

            await saveSearch({ name, comparisonType: 12, vendorCategoryItemIds: ids });
            return;
        }

        // Features / FCT
        if (saveableCtx.type === "features") {
            const ids = (saveableCtx.featureIds || []).map(Number).filter(Number.isFinite);
            if (!ids.length) return;

            const names = saveableCtx.featureNames;
            const suggested = Array.isArray(names) && names.length
                ? names.join(", ")
                : ids.join(",");

            const name = window.prompt("Save this search as…", suggested);
            if (!name) return;

            await saveSearch({ name, comparisonType: 2, featureIds: ids });
            return;
        }

        console.info("No savable context found.");
    }, [saveableCtx, saveSearch]);

    const COMPARISON_TYPE_PRODUCT = 1;

    async function handleSaveProductComparison(selectedProductIds: number[]) {
        if (!selectedProductIds.length) {
            alert("Select at least one product to save a comparison.");
            return;
        }

        const defaultName = "Product comparison";
        const result = await openSaveDialog({ defaultName });
        if (!result || !result.name) return;

        await saveSearch({
            name: result.name,
            shared: !!result.shared,
            comparisonType: COMPARISON_TYPE_PRODUCT,
            productIds: selectedProductIds,
        });
    }

    const saveProductComparisonFromDashboard = async (ids: number[], shared: boolean) => {
        const name = window.prompt("Save this product comparison as…");
        if (!name) return;

        await saveSearch({
            name,
            shared,
            comparisonType: 1,
            productIds: ids,
        });
    };

    const setModuleFeatureIds = useCallback((moduleId, ids) => {
        const mid = Number(moduleId);
        if (!Number.isFinite(mid)) return;

        setFeatureIdsByModule((prev) => ({
            ...prev,
            [mid]: Array.isArray(ids) ? ids : [],
        }));
    }, []);

    useEffect(() => {
        const onSaveComparisonFromTearsheet = (e) => {
            const raw = e?.detail?.productIds;
            if (!raw) return;

            const ids = Array.isArray(raw)
                ? raw
                : String(raw)
                    .split(/[,\s]+/)
                    .map(n => Number(n))
                    .filter(Number.isFinite);

            if (!ids.length) return;

            handleSaveProductComparison(ids);
        };

        window.addEventListener(
            "profiler:save-product-comparison",
            onSaveComparisonFromTearsheet
        );

        return () =>
            window.removeEventListener(
                "profiler:save-product-comparison",
                onSaveComparisonFromTearsheet
            );
    }, [handleSaveProductComparison]);

    function compareLabel( a, b ) {
      if ( a.label < b.label ){
        return -1;
      }
      if ( a.label > b.label ){
        return 1;
      }
      return 0;
   }

    useEffect(() => {
        const fetchAllFeatureHeaders = async () => {
            const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
            try {
                const moduleIdsToFetch = [1, 2, 3, 9, 10];

                const results = await Promise.all(
                    moduleIdsToFetch.map(async (id) => {
                        const res = await axios.get(
                            `${process.env.REACT_APP_API_URL}/features/headers/${id}`,
                            { headers }
                        );
                        const tabs = Object.entries(res.data).map(([fid, fname]) => ({
                            label: fname,
                            categoryId: parseInt(fid, 10),
                        })).sort(compareLabel);
                        return { moduleId: id, tabs };
                    })
                );

                const newMap = {};
                results.forEach(({ moduleId, tabs }) => (newMap[moduleId] = tabs));
                setFeatureTabsMap(newMap);
            } catch (error) {
                console.error("Error fetching feature headers:", error);
            }
        };
        fetchAllFeatureHeaders();
    }, []);

    useEffect(() => {
        const onRunKeywordSearch = (e) => {
            const detail = e?.detail || {};
            const keyword = (detail.keyword || "").trim();
            const productCategoryItemIds = (detail.productCategoryItemIds || [])
                .map(Number)
                .filter(Number.isFinite);
            const vendorCategoryItemIds = (detail.vendorCategoryItemIds || [])
                .map(Number)
                .filter(Number.isFinite);
            const featureIdsByModule = detail.featureIdsByModule && typeof detail.featureIdsByModule === "object"
                ? Object.entries(detail.featureIdsByModule).reduce((acc, [moduleId, ids]) => {
                    const mid = Number(moduleId);
                    const validIds = Array.isArray(ids) ? ids.map(Number).filter(Number.isFinite) : [];
                    if (Number.isFinite(mid)) acc[mid] = validIds;
                    return acc;
                }, {})
                : createEmptyFeatureIdsByModule();
            if (!keyword) return;

            // store for later
            pendingKeywordRef.current = keyword;
            pendingKeywordDetailRef.current = detail;

            // switch to Builder; ProfilerKeywordSearch will mount now
            setViewMode("Discovery Builder");
            setKeywordSearchValue(keyword);
            setDiscoverKeywordTarget(
                detail.mode === SEARCH_REQUEST_TYPES.PRODUCT
                    ? "products_only"
                    : detail.mode === SEARCH_REQUEST_TYPES.VENDOR
                        ? "vendors_only"
                        : "both"
            );
            setSelectedVendorCategoryIds(vendorCategoryItemIds);
            setSelectedProductCategoryIds(productCategoryItemIds);
            setFeatureIdsByModule({
                1: featureIdsByModule[1] || [],
                2: featureIdsByModule[2] || [],
                3: featureIdsByModule[3] || [],
            });
        };

        window.addEventListener("profiler:run-keyword-search", onRunKeywordSearch);
        return () =>
            window.removeEventListener("profiler:run-keyword-search", onRunKeywordSearch);
    }, []);

    useEffect(() => {
        const onRunCombinationSearch = (e) => {
            const detail = e?.detail || {};
            const keyword = String(detail.keyword || "").trim();
            const productCategoryItemIds = (detail.productCategoryItemIds || [])
                .map(Number)
                .filter(Number.isFinite);
            const vendorCategoryItemIds = (detail.vendorCategoryItemIds || [])
                .map(Number)
                .filter(Number.isFinite);
            const featureIdsByModule = detail.featureIdsByModule && typeof detail.featureIdsByModule === "object"
                ? Object.entries(detail.featureIdsByModule).reduce((acc, [moduleId, ids]) => {
                    const mid = Number(moduleId);
                    const validIds = Array.isArray(ids) ? ids.map(Number).filter(Number.isFinite) : [];
                    if (Number.isFinite(mid) && validIds.length) acc[mid] = validIds;
                    return acc;
                }, {})
                : {};

            setViewMode("Discovery Builder");
            setKeywordSearchValue(keyword);
            setDiscoverKeywordTarget("both");
            setSelectedProductCategoryIds(productCategoryItemIds);
            setSelectedVendorCategoryIds(vendorCategoryItemIds);
            setFeatureIdsByModule({
                1: featureIdsByModule[1] || [],
                2: featureIdsByModule[2] || [],
                3: featureIdsByModule[3] || [],
            });
        };

        window.addEventListener("profiler:run-combination-search", onRunCombinationSearch);
        return () =>
            window.removeEventListener("profiler:run-combination-search", onRunCombinationSearch);
    }, []);

    useEffect(() => {
        const onRunFeatureSearch = (e) => {
            const ids = e?.detail?.featureIds;
            if (!Array.isArray(ids) || !ids.length) return;

            const mid =
                Number(e?.detail?.moduleId) ||
                MODULE_IDS[activeRightTab] ||
                null;

            if (!mid) return;

            setViewMode("Discovery Builder");
            setActiveRightTab(MODULE_LABELS[mid] || "Commodity");

            setModuleFeatureIds(mid, ids);
        };

        window.addEventListener("profiler:run-feature-search", onRunFeatureSearch);
        return () =>
            window.removeEventListener("profiler:run-feature-search", onRunFeatureSearch);
    }, [activeRightTab, setModuleFeatureIds]);

    useEffect(() => {
        const onRunProductCategory = (e) => {
            const ids = e?.detail?.categoryItemIds;
            if (!Array.isArray(ids) || !ids.length) return;

            setViewMode("Discovery Builder");
            setActiveRightTab("Product Category");
            handleProductCategorySearch(ids);
        };

        window.addEventListener("profiler:run-product-category-search", onRunProductCategory);
        return () =>
            window.removeEventListener("profiler:run-product-category-search", onRunProductCategory);
    }, []);

    useEffect(() => {
        const onRunVendorCategory = (e) => {
            const ids = e?.detail?.categoryItemIds;
            if (!Array.isArray(ids) || !ids.length) return;

            setViewMode("Discovery Builder");
            setActiveRightTab("Vendor Category");
            handleVendorCategorySearch(ids);
        };

        window.addEventListener("profiler:run-vendor-category-search", onRunVendorCategory);
        return () =>
            window.removeEventListener("profiler:run-vendor-category-search", onRunVendorCategory);
    }, []);

    useEffect(() => {
        const onToggleCompare = (e: any) => {
            const detail = e?.detail || {};
            const id = Number(detail.id);
            const name = String(detail.name || "").trim();

            if (!Number.isFinite(id)) return;

            setCompareProducts(prev => {
                const exists = prev.some(p => p.id === id);
                if (exists) {
                    return prev.filter(p => p.id !== id);
                }
                return [...prev, { id, name: name || `Product ${id}` }];
            });
        };

        window.addEventListener("profiler:toggle-product-compare", onToggleCompare);
        return () =>
            window.removeEventListener("profiler:toggle-product-compare", onToggleCompare);
    }, []);

    useEffect(() => {
             if (viewMode !== "Discovery Builder") return;
             const kw = pendingKeywordRef.current;
             if (!kw) return;

                 let attempts = 0;
             const MAX = 40;
             const STEP = 50;

                 const tryInject = () => {
                   attempts += 1;
                   const input =
                         document.querySelector('input[aria-label="Keyword search"]') ||
                         document.querySelector('.searchbar-input') ||
                         document.querySelector('input[placeholder="Discover..."]');

                     if (input) {
                         const desc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value");
                         if (desc && desc.set) desc.set.call(input, kw);
                         else input.value = kw; // fallback

                         input.dispatchEvent(new Event("input", { bubbles: true }));

                         const detail = pendingKeywordDetailRef.current;
                         if (detail) {
                             window.dispatchEvent(
                                 new CustomEvent("profiler:keyword-search-internal", { detail })
                             );
                         }

                         pendingKeywordRef.current = null;
                         pendingKeywordDetailRef.current = null;
                         return;
                     }

                       if (attempts < MAX) setTimeout(tryInject, STEP);
                   else {
                         console.warn("ProfilerKeywordSearch input not found for injection.");
                         pendingKeywordRef.current = null;
                       }
                 };

                     setTimeout(tryInject, 0);
           }, [viewMode]);

    useEffect(() => {
        setSelectedVendorCategoryIds([]);
        setSelectedProductCategoryIds([]);
        setFeatureIdsByModule([]);
        setKeywordHighlight(null);
        setTearsheetVendorId(null);
        setProductsVendorId(null);
        setTearsheetProductId(null);
        setTearsheetDataId(null);
        setEsgCoverageEsgIds([]);
        setEsgCoverageAltIds([]);

        setActiveTearsheetTab("Vendor");
        setViewMode("Discovery Builder");

        setActiveRightTab(isAltDataMode ? "ESG Vendor Category" : "Vendor Category");
        setRightPanelResetKey(k => k + 1);
    }, [activeTab]);


    // LOGOUT
    const handleLogout = async () => {
        try {
            // Clear app state first
            tear.clearAllSheets();
            clearSession();

            // Clear all localStorage keys in one go
            const keysToRemove = [
                "token",
                "user",
                "xtvmAllowed",
                "altDataAllowed",
                "ibmAllowed",
                "ibmRestricted",
            ];
            keysToRemove.forEach((key) => localStorage.removeItem(key));

        } catch (err) {
            console.error("Logout cleanup error:", err);
        } finally {
            // This hard redirect replaces the entire app, so navigate() is unnecessary
            // ADD back in when SSO goes live
            //window.location.href = `${process.env.REACT_APP_API_URL}/saml/logout`;
        }
    };

// remove || label === "ALT DATA" when ALT DATA module is live
    const isProfilerRedirectTab = (label: string) => label === "XTVM";

    function getStoredTokenValue(): string {
        return getSessionToken() || "";
    }

    function getStoredIbmTokenValue(): string {
        return getSessionString("ibmToken");
    }

    function getIsIbmPermitted(): string {
        return getSessionString("ibmRestricted");
    }

    function getIsXtvmPermitted(): boolean {
        return getSessionBoolean("xtvmAllowed");
    }

    function getIsAltDataPermitted(): boolean {
        return getSessionBoolean("altDataAllowed");
    }

    function getIsIbmAllowed(): boolean {
        const val = getSessionString("ibmAllowed");
        if (val !== "") return val === "true";
        const legacy = getIsIbmPermitted();
        return legacy === "true";
    }

    function getStoredFullName(): string {
        return getSessionString("user");
    }

    function getStoredUserName(): string {
        return getSessionString("userName") || getStoredFullName();
    }

    function trimSlashRight(s: string) {
        return (s || "").replace(/\/+$/, "");
    }

    function ensureModuleAccess(label: string, allowed: boolean): boolean {
        if (allowed) return true;
        if (label === "ALT DATA") {
            alert("Not licensed for Alternative Data Module. Please contact 3di.");
            return false;
        }
        if (label === "Indices & Benchmarks") {
            alert("Not licensed for Indices & Benchmarks Module. Please contact 3di.");
            return false;
        }
        if (label === "XTVM") {
            alert("Not licensed for XTVM Module. Please contact 3di.");
            return false;
        }
        alert("Not licensed for this module. Please contact 3di.");
        return false;
    }

    


    async function openIndicesBenchmarks() {
       
        if (!ensureModuleAccess("Indices & Benchmarks", getIsIbmAllowed())) return;
        const base = trimSlashRight(localStorage.getItem("indicesUrl") || process.env.REACT_APP_IBM_URL || "");
        let url = base;
        const ibmPermitted = getIsIbmPermitted();
        if (ibmPermitted === "false") {
            window.open(url, "_blank", "noopener");
        } else {
            const token = getStoredIbmTokenValue();
            const username = getStoredUserName();
            if (!base || !token || !username) {
                console.warn("Missing indices base URL, token, or username.");
                return;
            }

            url = `${base}/auth/${encodeURIComponent(username)}/${encodeURIComponent(token)}`;

            window.open(url, "_blank", "noopener");
        }

    }


    function submitProfilerPost(redirectId, openInNewTab = true) {
        const template =
            localStorage.getItem("profilerUrl") ||
            process.env.REACT_APP_PROFILER_URL ||
            "";

        if (!template) {
            console.warn("Missing profilerUrl template (localStorage or env).");
            return;
        }

        const username = getStoredFullName();
        const token = getStoredTokenValue();

        const profilerLink = (function fillTemplate(tpl, repl) {
            let out = tpl;
            Object.entries(repl).forEach(([k, v]) => {
                out = out.split(`[${k}]`).join(encodeURIComponent(v ?? ""));
            });
            return out;
        })(template, {
            username,
            password: token,
            redirectid: redirectId != null ? `modetoopen=${redirectId}` : "",
        });

        const form = document.createElement("form");
        form.method = "POST";
        form.action = profilerLink;
        if (openInNewTab) form.target = "_blank";

        const tokenInput = document.createElement("input");
        tokenInput.type = "hidden";
        tokenInput.name = "token";
        tokenInput.value = token;
        form.appendChild(tokenInput);

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    }

    const handleClassicView = () => {
        // Profiler classic = 1, Alt Data classic = 3
        const redirectValue = isAltDataMode ? "3" : "1";

        submitProfilerPost(redirectValue, true)
    };

    // PRINT / DOWNLOAD
    const handlePrint = () => window.print();
    const handleDownloadWord = () => console.log("Download Word doc");
    const handleDownloadPdf = () => console.log("Download PDF");

    // SEARCH HANDLERS
    const handleVendorCategorySearch   = (ids) => {
        setSelectedVendorCategoryIds(ids);
    };
    const handleProductCategorySearch  = (ids) => {
        setSelectedProductCategoryIds(ids);
    };


    // TEARSHEET OPEN/CLOSE

    const openVendorTearsheet = useCallback((id) => {
        setTearsheetVendorId(id);
        setActiveTearsheetTab("Vendor");
    }, []);
    const openProductsTearsheet = useCallback((vid) => {
        setProductsVendorId(vid);
        setActiveTearsheetTab("Products");
    }, []);
    const openProductTearsheet = useCallback((id) => {
        setTearsheetProductId(id);
        setActiveTearsheetTab("Product");
    }, []);
    const openDataTearsheet = useCallback((id) => {
        setTearsheetDataId(id);
        setActiveTearsheetTab("Data");
    }, []);

    const openDataModalFromDashboard = React.useCallback((ids) => {
        window.dispatchEvent(new CustomEvent("open-profiler-data-modal", { detail: ids }));
    }, []);

    const runCurrentComparison = useCallback(() => {
        if (!compareProductIds.length) {
            alert("Please select at least one product to compare.");
            return;
        }
        openDataModalFromDashboard(compareProductIds.join(","));
    }, [compareProductIds, openDataModalFromDashboard]);

    const closeTearsheet = () => {
        setTearsheetVendorId(null);
        setProductsVendorId(null);
        setTearsheetProductId(null);
        setTearsheetDataId(null);
    };

    const renderTearsheet = () => {
        switch (activeTearsheetTab) {
            case "Vendor": return tearsheetVendorId && (
                <VendorTearsheet
                    vendorId={tearsheetVendorId}
                    contextPath={process.env.REACT_APP_API_URL}
                    onClose={closeTearsheet}
                    onOpenProductTearsheet={openProductTearsheet}
                    highlight={keywordHighlight}
                    mode={isAltDataMode ? "altdata" : "profiler"}
                />
            );
            case "Products": return productsVendorId && (
                <ProductsListTearsheet
                    vendorId={productsVendorId}
                    onClose={closeTearsheet}
                    onProductClick={openProductTearsheet}
                />
            );
            case "Product": return tearsheetProductId && (
                <ProductTearsheet
                    productId={tearsheetProductId}
                    contextPath={process.env.REACT_APP_API_URL}
                    onClose={closeTearsheet}
                    onOpenProductTearsheet={openProductTearsheet}
                    onOpenVendorTearsheet={openVendorTearsheet}
                    highlight={keywordHighlight}
                    mode={isAltDataMode ? "altdata" : "profiler"}
                />
            );
            case "Data": return tearsheetDataId && (
                <DataTearsheet
                    dataId={tearsheetDataId}
                    contextPath={process.env.REACT_APP_API_URL}
                    onClose={closeTearsheet}
                />
            );
            default: return null;
        }
    };

    const isAltDataProductCategory = isAltDataMode && activeRightTab === "Product Category";
    console.log("Rendering");
    return (
        <div className={`dashboard-wrapper h-screen flex flex-col overflow-hidden  ${isAltDataMode ? "alt-data" : ""}`}>
            <header className="dashboard-header h-[124px] w-full shrink-0">
                <div className="logo-tabs">
                    <img src={logo} alt="Logo" className="dashboard-logo" />
                    <div className="tab-bar">
                        {HEADER_TABS.map(tab => {
                            if (isProfilerRedirectTab(tab)) {
                                const redirectId = REDIRECT_IDS[tab];
                                const isAllowed =
                                    tab === "XTVM" ? getIsXtvmPermitted() : getIsAltDataPermitted();
                                return (
                                    <a
                                        key={tab}
                                        className="tab-button"
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (!ensureModuleAccess(tab, isAllowed)) return;
                                            submitProfilerPost(redirectId, true);
                                        }}
                                        title={`Open ${tab} in classic Profiler`}
                                    >
                                        {tab}
                                    </a>
                                );
                            }

                            if (tab === "Indices & Benchmarks") {
                                return (
                                    <a
                                        key={tab}
                                        className="tab-button"
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            openIndicesBenchmarks();
                                        }}
                                        title="Open Indices & Benchmarks"
                                    >
                                        {tab}
                                    </a>
                                );
                            }

                            return (
                                <button
                                    key={tab}
                                    className={`tab-button ${activeTab === tab ? "active" : ""}`}
                                    onClick={() => setActiveTab(tab)}
                                >{tab}</button>
                            );
                        })}
                    </div>

                </div>

                {/* Logout */}
                <div className="logout-section flex flex-col items-center">
                    <button className="logout-button flex items-center gap-2" onClick={handleLogout}>
                        <FiLogOut className="logout-icon" /> Logout
                    </button>
                    <span className="text-sm text-gray-500 mt-1">
                        {getStoredFullName()}
                    </span>
                </div>

            </header>

            {(activeTab === "Profiler" || activeTab === "ALT DATA") ? (
                <div className="dashboard-container-two flex h-[calc(100vh-124px)] overflow-hidden">
                    {/* LEFT PANEL */}
                    <div className="floating-section left-panel w-1/2">
                        <div className="card-tab-header flex items-center gap-2">
                            {/* Left tabs */}
                            <div className="flex items-center gap-2">
                                <div
                                    className={`card-tab ${viewMode==="Classic View"?"active":""}`}
                                    onClick={handleClassicView}
                                >
                                    <MdVisibility className="card-tab-icon" /> Classic View
                                </div>
                                <div
                                    className={`card-tab ${viewMode==="Discovery Builder"?"active":""}`}
                                    onClick={()=>setViewMode("Discovery Builder")}
                                >
                                    <MdBuildCircle className="card-tab-icon" /> Builder
                                </div>
                            </div>


                            <HeaderQuickMenu
                                isAltDataMode={isAltDataMode}
                                isExpertInsightsActive={viewMode === "Expert Insights"}
                                onOpenExpertInsights={() => setViewMode("Expert Insights")}
                                onPickQuickGuides={() => setViewMode("Quick Guides")}
                                onPickIndustryNews={() => setViewMode("Industry News")}
                                onPickParentCompany={() => setViewMode("Parent Company")}
                                onPickGlossary={() => setViewMode("Glossary")}
                                onPickWebsites={() => setViewMode("Websites")}
                                onPickTearsheets={() => setViewMode("Tearsheets")}
                                onPickNotesCommentary={() => setViewMode("Notes Commentary")}
                                onPickMyProfiler={() => setViewMode("My Profiler")}
                                onPickExchanges={() => setViewMode("Exchanges")}
                                onPickHelpSettings={() => setViewMode("Help Settings")}
                                onPickSettings={() => console.log("Settings clicked")}
                            />

                        </div>
                        {viewMode === "Discovery Builder" && compareProducts.length > 0 && (
                            <div className="mt-4 mb-3 p-3 rounded-xl border border-[rgba(232,119,34,0.3)] bg-orange-50/40 text-xs">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-semibold text-[var(--rusty-orange)]">
                                        Product comparison ({compareProducts.length} selected)
                                    </div>
                                    <button
                                        type="button"
                                        className="underline text-[11px] text-[var(--threedi-blue,#25408f)]"
                                        onClick={() => {
                                            setCompareProducts([]);
                                            window.dispatchEvent(
                                                new CustomEvent("profiler:clear-product-compare")
                                            );
                                        }}
                                    >
                                        Clear
                                    </button>
                                </div>

                                <div className="flex flex-wrap gap-2 mb-3">
                                    {compareProducts.map(p => (
                                        <span
                                            key={p.id}
                                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-[rgba(37,64,143,0.25)] text-[11px]"
                                        >
                    <span className="max-w-[140px] truncate" title={p.name}>
                        {p.name}
                    </span>
                    <button
                        type="button"
                        onClick={() =>
                            window.dispatchEvent(
                                new CustomEvent("profiler:toggle-product-compare", {
                                    detail: { id: p.id, name: p.name },
                                })
                            )
                        }
                        className="leading-none"
                        title="Remove from comparison"
                    >
                        ×
                    </button>
                </span>
                                    ))}
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={runCurrentComparison}
                                        className="px-3 py-1.5 rounded-lg border border-[var(--threedi-blue,#25408f)] text-[var(--threedi-blue,#25408f)] text-xs font-semibold hover:bg-[var(--threedi-blue,#25408f)]/5"
                                    >
                                        Run comparison
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleSaveProductComparison(compareProductIds)}
                                        className="px-3 py-1.5 rounded-lg border border-[var(--rusty-orange)] text-[var(--rusty-orange)] text-xs font-semibold hover:bg-[var(--rusty-orange)]/10"
                                    >
                                        Save comparison
                                    </button>
                                </div>
                            </div>
                        )}

                        {viewMode === "Discovery Builder" && (
                            <MemoProfilerKeywordSearch
                                mode={mode}
                                featureIds={allFeatureIds}
                                featureIdsByModule={featureIdsByModule}
                                vendorCategoryIds={selectedVendorCategoryIds}
                                productCategoryIds={selectedProductCategoryIds}
                                hasSubmittedSearch={hasSubmittedSearch}
                                onHasSubmittedSearchChange={setHasSubmittedSearch}
                                openSheet={tear.openSheet}
                                onKeywordSearch={handleBeforeKeywordSearch}
                                onClearAll={handleClearAll}
                                clearResultsToken={searchResultsClearToken}
                                keywordValue={keywordSearchValue}
                                onKeywordChange={setKeywordSearchValue}
                                searchRequestHistory={searchRequestHistory}
                                replaySearchHistoryToken={searchHistoryReplayToken}
                                discoverKeywordTargetValue={discoverKeywordTarget}
                                onDiscoverKeywordTargetChange={setDiscoverKeywordTarget}
                                onSearchRequestHistoryUpsert={upsertSearchRequestHistory}
                                onSearchRequestHistoryReplace={replaceSearchRequestHistory}
                            />
                        )}
                        {viewMode === "Expert Insights" && (
                            <MemoExpertInsights
                                contextPath={process.env.REACT_APP_API_URL}
                                mode={mode}
                                openSheet={tear.openSheet}
                            />
                        )}
                        {viewMode === "Quick Guides" && (
                            <MemoQuickGuides
                                contextPath={process.env.REACT_APP_API_URL}
                                mode={mode}
                                openSheet={tear.openSheet}
                            />
                        )} {viewMode === "Industry News" && (
                        <MemoIndustryNews
                            contextPath={process.env.REACT_APP_API_URL}
                            mode={mode}
                            openSheet={tear.openSheet}
                        />
                    )}
                        {viewMode === "Parent Company" && (
                            <MemoParentCompany
                                contextPath={process.env.REACT_APP_API_URL}
                                mode={mode}
                                openSheet={tear.openSheet}
                                onOpenVendorTearsheet={openVendorTearsheet}
                            />
                        )}
                        {viewMode === "Glossary" && (
                            <MemoGlossary
                                contextPath={process.env.REACT_APP_API_URL}
                                 mode={mode}
                                openSheet={tear.openSheet}
                            />
                        )}
                        {viewMode === "Websites" && (
                            <MemoWebsites
                                contextPath={process.env.REACT_APP_API_URL}
                                 mode={mode}
                            />
                        )}
                        {viewMode === "Tearsheets" && (
                            <MemoTearsheets
                                contextPath={process.env.REACT_APP_API_URL}
                                 mode={mode}
                                openSheet={tear.openSheet}
                            />
                        )}
                        {viewMode === "Notes Commentary" && (
                            <MemoNotesCommentary
                                contextPath={process.env.REACT_APP_API_URL}
                                 mode={mode}
                                openSheet={tear.openSheet}
                            />
                        )}
                        {viewMode === "My Profiler" && (
                            <MemoMyProfiler
                                apiBase={process.env.REACT_APP_API_URL}
                                 mode={mode}
                                onOpenVendorTearsheet={openVendorTearsheet}
                                onOpenProductTearsheet={openProductTearsheet}
                                onOpenDataModal={openDataModalFromDashboard}
                            />
                        )}
                        {viewMode === "Exchanges" && (
                            <MemoExchanges
                                apiBase={process.env.REACT_APP_API_URL}
                                 mode={mode}
                                openSheet={tear.openSheet}
                            />
                        )}
                        {viewMode === "Help Settings" && (
                            <MemoHelpSettingsPage
                                apiBase={process.env.REACT_APP_API_URL}
                            />
                        )}


                    </div>

                    {/* RIGHT PANEL */}
                    <div className="floating-section right-panel w-1/2 flex flex-col overflow-y-auto">
                        {(tearsheetVendorId || productsVendorId || tearsheetProductId || tearsheetDataId) ? (
                            <RightPanel
                                activeTearsheetTab={activeTearsheetTab}
                                setActiveTearsheetTab={setActiveTearsheetTab}
                                tearsheetVendorId={tearsheetVendorId}
                                productsVendorId={productsVendorId}
                                tearsheetProductId={tearsheetProductId}
                                tearsheetDataId={tearsheetDataId}
                                openProductsTearsheet={openProductsTearsheet}
                                openProductTearsheet={openProductTearsheet}
                                openDataTearsheet={openDataTearsheet}
                                handlePrint={handlePrint}
                                handleDownloadWord={handleDownloadWord}
                                handleDownloadPdf={handleDownloadPdf}
                                highlight={keywordHighlight}
                                mode={isAltDataMode ? "altdata" : "profiler"}
                            />
                        ) : (
                            <>
                                <div className="card-tab-header right-tab-header">
                                    {rightTabs.map(({label,icon})=> (
                                        <div
                                            key={label}
                                            className={`card-tab ${activeRightTab === label ? "active" : ""}`}
                                            onClick={() => setActiveRightTab(label)}
                                        >
                                            <span className="card-tab-icon">{icon}</span>
                                            {label}
                                        </div>
                                    ))}
                                </div>

                                 <div className="right-panel-body flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-3">
                                    <div className="tab-content" key={rightPanelResetKey}>
                                        {/* CATEGORY CARDS */}
                                        {(["Vendor Category", "Product Category", "ESG Vendor Category"].includes(activeRightTab)) && (
                                            <TabbedCategoryCardGroup
                                                type="category"
                                                mode={mode}
                                                moduleId={isAltDataProductCategory ? 9 : null}
                                                hasSubmittedSearch={hasSubmittedSearch}
                                                onHasSubmittedSearchChange={setHasSubmittedSearch}
                                                categoryTabs={
                                                    (activeRightTab === "Vendor Category" || activeRightTab === "ESG Vendor Category")
                                                        ? vendorTabs
                                                        : productTabs
                                                }
                                                selectedItemsMap={
                                                    (activeRightTab === "Vendor Category" || activeRightTab === "ESG Vendor Category")
                                                        ? selectedVendorCategoryIds
                                                        : selectedProductCategoryIds
                                                }
                                                onItemSelectionChange={
                                                    (activeRightTab === "Vendor Category" || activeRightTab === "ESG Vendor Category")
                                                        ? setSelectedVendorCategoryIds
                                                        : setSelectedProductCategoryIds
                                                }
                                                onSearch={
                                                    (activeRightTab === "Vendor Category" || activeRightTab === "ESG Vendor Category")
                                                        ? handleVendorCategorySearch
                                                        : handleProductCategorySearch
                                                }
                                                setViewMode={setViewMode}
                                            />
                                        )}

                                        {/* FEATURE CARDS */}
                                        {(["Fixed Income", "Equity", "Commodity", "Data/Functionality Coverage", "ESG Data/Functionality Coverage"].includes(activeRightTab)) && (
                                       <TabbedCategoryCardGroup
                                            type="feature"
                                            mode={mode}
                                            moduleId={moduleId}
                                            hasSubmittedSearch={hasSubmittedSearch}
                                            onHasSubmittedSearchChange={setHasSubmittedSearch}
                                        categoryTabs={featureTabsMap[moduleId] || []}
                                        selectedItemsMap={featureIdsByModule[moduleId] || []}
                                        onItemSelectionChange={(ids) =>
                                            setFeatureIdsByModule((prev) => ({ ...prev, [moduleId]: ids }))
                                        }
                                        onSearch={(ids) =>
                                            setFeatureIdsByModule((prev) => ({ ...prev, [moduleId]: ids }))
                                        }
                                        setViewMode={setViewMode}
                                    />
                                     )}
                                    </div>


                                     <SelectedFiltersTray
                                         vendorIds={selectedVendorCategoryIds}
                                         productIds={selectedProductCategoryIds}
                                         featureIdsByModule={featureIdsByModule}
                                         categoryItemNameCache={categoryItemNameCache}
                                         featureNameCache={featureNameCache}
                                         keywordRequests={keywordRequestItems}
                                         onRemoveVendor={(id) => {
                                             const nextVendorIds = selectedVendorCategoryIds.filter((x) => x !== id);
                                             setSelectedVendorCategoryIds(nextVendorIds);
                                             replaySearchRequestHistory(
                                                 searchRequestHistoryRef.current
                                                     .map((request) => {
                                                         if (request?.historyType !== "vendor_category") return request;
                                                         const nextPayload = {
                                                             ...(request?.payload || {}),
                                                             vendorCategoryIds: nextVendorIds,
                                                         };
                                                         return nextVendorIds.length
                                                             ? { ...request, payload: nextPayload }
                                                             : null;
                                                     })
                                                     .filter(Boolean)
                                             );
                                         }}
                                         onRemoveProduct={(id) => {
                                             const nextProductIds = selectedProductCategoryIds.filter((x) => x !== id);
                                             setSelectedProductCategoryIds(nextProductIds);
                                             replaySearchRequestHistory(
                                                 searchRequestHistoryRef.current
                                                     .map((request) => {
                                                         if (request?.historyType !== "product_category") return request;
                                                         const nextPayload = {
                                                             ...(request?.payload || {}),
                                                             productCategoryIds: nextProductIds,
                                                         };
                                                         return nextProductIds.length
                                                             ? { ...request, payload: nextPayload }
                                                             : null;
                                                     })
                                                     .filter(Boolean)
                                             );
                                         }}
                                         onRemoveFeature={(moduleId, id) => {
                                             const nextFeatureIdsByModule = {
                                                 ...featureIdsByModule,
                                                 [moduleId]: (featureIdsByModule?.[moduleId] || []).filter((x) => x !== id),
                                             };
                                             const nextFeatureIds = Object.values(nextFeatureIdsByModule || {})
                                                 .flat()
                                                 .map(Number)
                                                 .filter(Number.isFinite);

                                             setFeatureIdsByModule(nextFeatureIdsByModule);
                                             replaySearchRequestHistory(
                                                 searchRequestHistoryRef.current
                                                     .map((request) => {
                                                         if (request?.historyType !== "feature") return request;
                                                         const nextPayload = {
                                                             ...(request?.payload || {}),
                                                             featureIds: nextFeatureIds,
                                                         };
                                                         return nextFeatureIds.length
                                                             ? { ...request, payload: nextPayload }
                                                             : null;
                                                     })
                                                     .filter(Boolean)
                                             );
                                         }}
                                         onRemoveKeywordItem={(requestToRemove) => {
                                             const historyIndex = Number(requestToRemove?._historyIndex);
                                             if (!Number.isInteger(historyIndex) || historyIndex < 0) return;

                                             const nextHistory = searchRequestHistoryRef.current.filter((_, index) => index !== historyIndex);
                                             const remainingKeywordRequests = nextHistory.filter((request) => {
                                                 const historyType = String(request?.historyType || "").trim().toLowerCase();
                                                 const keyword = String(request?.payload?.keyword || "").trim();
                                                 return (
                                                     (
                                                         historyType === SEARCH_REQUEST_TYPES.DISCOVER ||
                                                         historyType === SEARCH_REQUEST_TYPES.PRODUCT ||
                                                         historyType === SEARCH_REQUEST_TYPES.VENDOR
                                                     ) &&
                                                     keyword.length > 0
                                                 );
                                             });

                                             const latestKeywordRequest =
                                                 remainingKeywordRequests.length > 0
                                                     ? remainingKeywordRequests[remainingKeywordRequests.length - 1]
                                                     : null;

                                             setKeywordSearchValue(String(latestKeywordRequest?.payload?.keyword || "").trim());
                                             setDiscoverKeywordTarget(
                                                 String(latestKeywordRequest?.payload?.keywordSearchType || "").trim() || "both"
                                             );

                                             replaySearchRequestHistory(nextHistory);
                                         }}
                                         onClearFilters={() => {
                                             setSearchResultsClearToken((value) => value + 1);
                                             handleClearAll();
                                         }}
                                     />
                                </div>
                            </>
                        )}
                    </div>

                </div>
            ) : (
                <div style={{ color:'#888', fontStyle:'italic' }}>
                    Content for <strong>{activeTab}</strong> in progress …
                </div>
            )}
            <TearsheetDock
                {...tear}
                contextPath={process.env.REACT_APP_API_URL}
                highlight={keywordHighlight}
                mode={mode}
            />
            {saveDialogPortal}
        </div>
    );
}
